import { useState } from "react";
import { CreateEmissionsSourceFormValues, defaultValues } from "./FormValues";
import Step1BasicInfo from "./Step1BasicInfo";
import Step2Type from "./Step2Type";
import Step3DataStatus from "./Step3Status";
import ArrowStepper from "../ArrowStepper";
import { useLocations } from "../../api/location";
import { useEmissionsSourceItems, usePortfolios, useSectors } from "../../api/emissions-sources";
import PreviousSelectionsBox from "./PreviousSelectionsBox";
import CancelDialog from "../CancelDialog";
import { useDialogBoxState } from "../DialogBox";

type CreateEmissionsSourceWizardProps = {
    onSubmit: (values: CreateEmissionsSourceFormValues) => void;
    onCancel: () => void;
    isSubmitting: boolean;
};

const CreateEmissionsSourceWizard = ({
    onSubmit,
    onCancel,
    isSubmitting,
}: CreateEmissionsSourceWizardProps) => {
    const [formData, setFormData] =
        useState<CreateEmissionsSourceFormValues>(defaultValues);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const cancelDialogState = useDialogBoxState();

    const handleCancel = () => {
        const formDataKeys = Object.keys(formData);

        for (const key of formDataKeys) {
            const typedObjectKey = key as keyof CreateEmissionsSourceFormValues;

            if (formData[typedObjectKey] !== defaultValues[typedObjectKey]) {
                cancelDialogState.open();
                return;
            }
        }

        onCancel();
    };

    const updateAction = (data: Partial<CreateEmissionsSourceFormValues>) => {
        setFormData((oldValue) => ({
            ...oldValue,
            ...data,
        }));
    };

    const handleBackClick = (
        data: Partial<CreateEmissionsSourceFormValues>
    ) => {
        updateAction(data);
        setSelectedIndex((value) => value - 1);
        window.scrollTo(0, 0);
    };

    const handleStepSubmit = (
        data: Partial<CreateEmissionsSourceFormValues>,
        finished?: boolean
    ) => {
        if (finished) {
            onSubmit({
                ...formData,
                ...data,
            });
        } else {
            updateAction(data);
            setSelectedIndex((value) => value + 1);
            window.scrollTo(0, 0);
        }
    };

    const { locations, isLoading: isLoadingLocations } = useLocations();
    const { sectors, isLoading: isLoadingSectors } = useSectors();
    const { portfolios, isLoading: isLoadingPortfolios } = usePortfolios();
    const { emissionsSourceItems, isLoading: isLoadingEmissionsSourceItems } =
        useEmissionsSourceItems();

    const pages = [
        <Step1BasicInfo
            onSubmit={handleStepSubmit}
            onCancel={handleCancel}
            defaultValues={formData}
            locations={locations}
            isLoadingLocations={isLoadingLocations}
            portfolios={portfolios}
            sectors={sectors}
            isLoadingPortfolios={isLoadingPortfolios}
            isLoadingSectors={isLoadingSectors}
        />,
        <Step2Type
            onSubmit={handleStepSubmit}
            onBack={handleBackClick}
            onCancel={handleCancel}
            defaultValues={formData}
            emissionsSourceItems={emissionsSourceItems}
            isLoadingEmissionsSourceItems={isLoadingEmissionsSourceItems}
        />,
        <Step3DataStatus
            onSubmit={handleStepSubmit}
            onBack={handleBackClick}
            onCancel={handleCancel}
            isSubmitting={isSubmitting}
            defaultValues={formData}
        />,
    ];

    return (
        <div className="space-y-5 max-w-xl mx-auto">
            <ArrowStepper
                steps={[
                    { stepNumber: 0, stepLabel: "1. Profile" },
                    { stepNumber: 1, stepLabel: "2. Attribution" },
                    { stepNumber: 2, stepLabel: "3. Data Collection" },
                ]}
                currentStep={selectedIndex}
            />
            {selectedIndex > 0 && (
                <PreviousSelectionsBox
                    formData={formData}
                    selectedIndex={selectedIndex}
                    locations={locations}
                    emissionsSourceItems={emissionsSourceItems}
                    sectors={sectors}
                    portfolios={portfolios}
                />
            )}
            {pages[selectedIndex]}
            <CancelDialog
                isOpen={cancelDialogState.isOpen}
                onClose={cancelDialogState.close}
                onCancel={onCancel}
            />
        </div>
    );
};

export default CreateEmissionsSourceWizard;
